/**
 * SkillsCatalog Component
 *
 * Modal browser for installed skills. Searchable flat list; clicking
 * Use pre-fills the chat composer with `/<name> ` so the user can send
 * the slash command to Claude Code. Distinct from the admin install/
 * manage flow in Settings → Skills.
 *
 * See `docs/adr/0005-skills-single-artifact-and-catalog.md` for the
 * binding design decisions.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api, type SkillInfo } from '@/services/api';
import { useChatStore } from '@/stores/chatStore';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Loader2, Search, Sparkles, X } from 'lucide-react';

interface SkillsCatalogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional: switch to chat tab when a skill is used (Layout wires this). */
  onUse?: () => void;
  /** Optional: open the Settings → Skills section from the empty state. */
  onOpenSettings?: () => void;
}

/** Pull the first sentence from a description; fall back to the full string. */
function firstSentence(text: string): { lead: string; rest: string | null } {
  const trimmed = text.trim();
  if (!trimmed) return { lead: '', rest: null };
  const match = trimmed.match(/^([^.!?]+[.!?])\s+(.+)$/s);
  if (!match) return { lead: trimmed, rest: null };
  return { lead: match[1], rest: match[2] };
}

export function SkillsCatalog({ isOpen, onClose, onUse, onOpenSettings }: SkillsCatalogProps) {
  const setPendingComposerText = useChatStore((s) => s.setPendingComposerText);

  const [skills, setSkills] = useState<SkillInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [expandedName, setExpandedName] = useState<string | null>(null);
  const [showMoreName, setShowMoreName] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, string>>({});
  const [detailsLoading, setDetailsLoading] = useState<string | null>(null);
  const [detailsError, setDetailsError] = useState<Record<string, string>>({});

  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<string, HTMLLIElement | null>>(new Map());

  // Fetch skills on every open (no caching per ADR-0005).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setQuery('');
    setSelectedIdx(0);
    setExpandedName(null);
    setShowMoreName(null);
    api.skills
      .list()
      .then((response) => {
        if (cancelled) return;
        setSkills(response.skills);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load skills');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Autofocus search on open
  useEffect(() => {
    if (isOpen) {
      // requestAnimationFrame ensures the input is mounted before focus.
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [isOpen]);

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedIdx(0);
  }, [query]);

  const filtered = useMemo(() => {
    const sorted = [...skills].sort((a, b) => {
      const ta = (a.title || a.name).toLowerCase();
      const tb = (b.title || b.name).toLowerCase();
      if (ta !== tb) return ta < tb ? -1 : 1;
      return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
    });
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((s) => {
      const hay = `${s.title || ''}\n${s.name}\n${s.description || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [skills, query]);

  // Clamp selectedIdx into filtered range
  useEffect(() => {
    if (selectedIdx >= filtered.length && filtered.length > 0) {
      setSelectedIdx(filtered.length - 1);
    }
  }, [filtered.length, selectedIdx]);

  // Scroll selected row into view
  useEffect(() => {
    if (!isOpen) return;
    const row = filtered[selectedIdx];
    if (!row) return;
    const el = rowRefs.current.get(row.name);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx, filtered, isOpen]);

  const handleUse = useCallback(
    (skill: SkillInfo) => {
      setPendingComposerText(`/${skill.name} `);
      onClose();
      onUse?.();
    },
    [setPendingComposerText, onClose, onUse]
  );

  const handleToggleExpand = useCallback(
    async (skill: SkillInfo) => {
      if (expandedName === skill.name) {
        setExpandedName(null);
        return;
      }
      setExpandedName(skill.name);
      if (!details[skill.name] && detailsLoading !== skill.name) {
        setDetailsLoading(skill.name);
        try {
          const detail = await api.skills.get(skill.name);
          setDetails((prev) => ({ ...prev, [skill.name]: detail.fullContent }));
          setDetailsError((prev) => {
            const { [skill.name]: _omit, ...rest } = prev;
            return rest;
          });
        } catch (err) {
          setDetailsError((prev) => ({
            ...prev,
            [skill.name]: err instanceof Error ? err.message : 'Failed to load skill',
          }));
        } finally {
          setDetailsLoading((current) => (current === skill.name ? null : current));
        }
      }
    },
    [expandedName, details, detailsLoading]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx((idx) => Math.min(filtered.length - 1, idx + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx((idx) => Math.max(0, idx - 1));
        return;
      }
      if (e.key === 'Enter') {
        const sel = filtered[selectedIdx];
        if (sel) {
          e.preventDefault();
          handleUse(sel);
        }
      }
    },
    [filtered, selectedIdx, handleUse, onClose]
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]"
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative bg-white dark:bg-surface-900 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Skills Catalog"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200 dark:border-surface-800">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-surface-900 dark:text-surface-100">
              Skills Catalog
            </h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-surface-200 dark:border-surface-800">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-surface-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search skills…"
              className={cn(
                'w-full rounded-md pl-9 pr-3 py-2 text-sm',
                'bg-surface-100 dark:bg-surface-800',
                'text-surface-900 dark:text-surface-100',
                'placeholder:text-surface-500 dark:placeholder:text-surface-400',
                'border border-surface-200 dark:border-surface-700',
                'focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500'
              )}
              aria-label="Search skills"
            />
          </div>
        </div>

        {/* Body */}
        <div ref={listRef} className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-12 text-surface-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading skills…
            </div>
          )}

          {error && !loading && (
            <div className="px-4 py-6 text-sm text-destructive-600 dark:text-destructive-400">
              {error}
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="px-4 py-8 text-sm text-surface-500 dark:text-surface-400">
              {query.trim() ? (
                <>
                  No skills match{' '}
                  <span className="font-mono text-surface-700 dark:text-surface-300">
                    “{query.trim()}”
                  </span>
                  .
                  {onOpenSettings && (
                    <>
                      {' '}Browse installed skills in{' '}
                      <button
                        type="button"
                        className="underline text-primary-600 dark:text-primary-400 hover:text-primary-700"
                        onClick={() => {
                          onClose();
                          onOpenSettings();
                        }}
                      >
                        Settings → Skills
                      </button>
                      .
                    </>
                  )}
                </>
              ) : (
                <>No skills installed yet.</>
              )}
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <ul className="divide-y divide-surface-200 dark:divide-surface-800">
              {filtered.map((skill, idx) => {
                const isSelected = idx === selectedIdx;
                const isExpanded = expandedName === skill.name;
                const fallbackTitle = skill.title || skill.name;
                const slashName = `/${skill.name}`;
                // Avoid double-printing when title is already the slash name
                const titleMatchesSlash = fallbackTitle === slashName;
                const desc = skill.description || '';
                const { lead, rest } = firstSentence(desc);
                const isShowingMore = showMoreName === skill.name;

                return (
                  <li
                    key={skill.name}
                    ref={(el) => {
                      if (el) rowRefs.current.set(skill.name, el);
                      else rowRefs.current.delete(skill.name);
                    }}
                  >
                    <div
                      className={cn(
                        'px-4 py-3 transition-colors',
                        isSelected
                          ? 'bg-primary-50 dark:bg-primary-900/20'
                          : 'hover:bg-surface-100 dark:hover:bg-surface-800/60'
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedIdx(idx);
                            handleToggleExpand(skill);
                          }}
                          className="flex-1 min-w-0 text-left"
                        >
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-surface-900 dark:text-surface-100 truncate">
                              {fallbackTitle}
                            </span>
                            {!titleMatchesSlash && (
                              <span className="text-xs font-mono text-surface-500 dark:text-surface-400">
                                {slashName}
                              </span>
                            )}
                          </div>
                          {desc && (
                            <p className="mt-1 text-xs text-surface-600 dark:text-surface-400">
                              {isShowingMore || !rest ? desc : lead}
                              {rest && (
                                <button
                                  type="button"
                                  className="ml-1 text-primary-600 dark:text-primary-400 hover:underline"
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    setShowMoreName(isShowingMore ? null : skill.name);
                                  }}
                                >
                                  {isShowingMore ? 'Show less' : 'Show more'}
                                </button>
                              )}
                            </p>
                          )}
                        </button>
                        <Button
                          size="sm"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            handleUse(skill);
                          }}
                          className="flex-shrink-0"
                        >
                          Use
                        </Button>
                      </div>

                      {isExpanded && (
                        <div className="mt-3 ml-0 rounded-md border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-950/40 px-3 py-2">
                          {detailsLoading === skill.name && (
                            <div className="flex items-center text-xs text-surface-500">
                              <Loader2 className="h-3 w-3 animate-spin mr-2" />
                              Loading SKILL.md…
                            </div>
                          )}
                          {detailsError[skill.name] && (
                            <div className="text-xs text-destructive-600 dark:text-destructive-400">
                              {detailsError[skill.name]}
                            </div>
                          )}
                          {details[skill.name] && (
                            <article className="prose prose-sm dark:prose-invert max-w-none">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {details[skill.name]}
                              </ReactMarkdown>
                            </article>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-surface-200 dark:border-surface-800 text-xs text-surface-500 dark:text-surface-400 flex items-center justify-between">
          <span>
            <kbd className="px-1.5 py-0.5 rounded bg-surface-200 dark:bg-surface-700 font-mono text-[10px]">
              ↑ ↓
            </kbd>{' '}
            move ·{' '}
            <kbd className="px-1.5 py-0.5 rounded bg-surface-200 dark:bg-surface-700 font-mono text-[10px]">
              Enter
            </kbd>{' '}
            use ·{' '}
            <kbd className="px-1.5 py-0.5 rounded bg-surface-200 dark:bg-surface-700 font-mono text-[10px]">
              Esc
            </kbd>{' '}
            close
          </span>
          <span>{filtered.length} skill{filtered.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  );
}

export default SkillsCatalog;
